// Category-aware catalog item form.
import { useEffect, useMemo } from 'react';
import { Alert, Button, Form, Input, Select, Space, Switch } from 'antd';
import type {
  CatalogCategoryResponse,
  CatalogoResponse,
  CreateCatalogoDto,
  UpdateCatalogoDto,
} from '@aid/shared';
import { useCatalogos } from '../api/useCatalogos';
import { useCategoriasList } from '../api/useCatalogosAdmin';

interface CatalogoFormProps {
  mode: 'create' | 'edit';
  initialValues?: CatalogoResponse | null;
  category?: CatalogCategoryResponse | null;
  categoryCode?: string;
  onSubmit: (values: CreateCatalogoDto | UpdateCatalogoDto) => void;
  loading?: boolean;
}

export function CatalogoForm({
  mode,
  initialValues,
  category,
  categoryCode,
  onSubmit,
  loading,
}: CatalogoFormProps) {
  const [form] = Form.useForm();
  const { data: categories, isError: categoriesError } = useCategoriasList();
  const selectedCategoryCode = Form.useWatch('category', form) as string | undefined;
  const resolvedCategoryCode = selectedCategoryCode ?? category?.code ?? categoryCode;
  const resolvedCategory =
    category ?? categories?.find((item) => item.code === resolvedCategoryCode) ?? null;
  const parentCategoryCode = resolvedCategory?.parentCategoryCode ?? undefined;
  const hasHierarchy = Boolean(resolvedCategory?.behavior.hierarchy.enabled);
  const { data: parentItems, isLoading: parentItemsLoading } = useCatalogos(
    parentCategoryCode ?? '',
    { limit: 1000, enabled: hasHierarchy && Boolean(parentCategoryCode) },
  );

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        key: initialValues.key ?? '',
        value: initialValues.value ?? '',
        category: initialValues.category ?? '',
        parent: initialValues.parent == null ? null : Number(initialValues.parent),
        active: initialValues.active,
      });
    } else if (categoryCode) {
      form.setFieldValue('category', categoryCode);
    }
  }, [categoryCode, form, initialValues]);

  const categoryOptions = useMemo(
    () =>
      (categories ?? [])
        .filter(
          (item) =>
            item.active || item.code === initialValues?.category || item.code === categoryCode,
        )
        .map((item) => ({ label: item.name, value: item.code })),
    [categories, categoryCode, initialValues?.category],
  );

  const parentOptions = useMemo(
    () =>
      (parentItems ?? []).map((item) => ({
        label: item.value ?? item.key ?? `#${item.id}`,
        value: Number(item.id),
      })),
    [parentItems],
  );

  const handleFinish = (values: CreateCatalogoDto | UpdateCatalogoDto) => {
    onSubmit(values);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      style={{ maxWidth: 600, width: '100%' }}
      initialValues={{ active: true, parent: null, category: categoryCode }}
    >
      {categoriesError && (
        <Alert
          type="warning"
          showIcon
          message="No se pudo actualizar la lista de categorías"
          description="Podés continuar editando este registro, pero no se pueden seleccionar nuevas categorías."
          style={{ marginBottom: 16 }}
        />
      )}
      <Form.Item
        name="key"
        label="Clave"
        rules={[{ required: true, message: 'La clave es obligatoria' }]}
      >
        <Input disabled={mode === 'edit' && resolvedCategory?.systemManaged} />
      </Form.Item>
      <Form.Item
        name="value"
        label="Valor visible"
        rules={[{ required: true, message: 'El valor es obligatorio' }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="category"
        label="Categoría"
        rules={[{ required: true, message: 'La categoría es obligatoria' }]}
      >
        <Select
          disabled={mode === 'edit'}
          placeholder="Seleccioná una categoría"
          options={categoryOptions}
          onChange={() => form.setFieldValue('parent', null)}
          showSearch
          optionFilterProp="label"
        />
      </Form.Item>
      {hasHierarchy && parentCategoryCode && (
        <Form.Item
          name="parent"
          label={`Padre (${parentCategoryCode})`}
          rules={[{ required: true, message: 'El registro padre es obligatorio' }]}
        >
          <Select<number>
            loading={parentItemsLoading}
            placeholder={`Seleccioná un ${parentCategoryCode.toLowerCase()}`}
            options={parentOptions}
            showSearch
            optionFilterProp="label"
            allowClear={false}
          />
        </Form.Item>
      )}
      {mode === 'edit' && (
        <Form.Item name="active" label="Estado" valuePropName="checked">
          <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
        </Form.Item>
      )}
      <Form.Item>
        <Space wrap size={[8, 8]}>
          <Button type="primary" htmlType="submit" loading={loading}>
            {mode === 'create' ? 'Crear registro' : 'Guardar cambios'}
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
}
